import { getBaseEndpoint } from "../../../features/endpoints/endpointManager"
import {
  getPracticeRoles,
  selectIsAffiliate,
  selectIsAffiliateRelations,
} from "../../Account/userSlice"
import {
  PracticeState,
  initialPractice,
  initialPracticeAddress,
  initialPracticeContact,
  practiceSlice,
  selectPracticeCompleteness,
} from "../model"
import { practiceLoaderSlice } from "./loader"
import { createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { RootState } from "../../../reduxStore/store"
import {
  PhoneEmailFax,
  Practice,
  PracticeInvite,
  PracticeStatus,
} from "../schema"
import { ContactTypeMap } from "../../CMS/schema"
import { fetchPracticeTags } from "./basePractice"
import { cloneDeep } from "lodash"
import {
  createBillingAddressPayload,
  createPatchBasePracticeArgs,
} from "./helpers"
import { DataStatus } from "../../enums"
import { PracticeTypeDisplayId } from "../../enums/displayId.enum"
import appInsightsLogger from "../../../features/logging/appInsightsLogger"

export const getBasePracticeEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}`
}
const getPracticeContactInfoEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/contactinfo`
}

export interface BasePracticeResponse {
  id: string
  isVisible: boolean
  name: string
  legacyAffiliateId: number | null
  status: PracticeStatus
  websiteUrl: string | null
  overview: string | null
  logoImageUrl: string | null
  authReleaseRequired: boolean | null
  hasOnlineServicesInCalifornia: boolean | null
  hasNoPhysicalLocations: boolean | null
  isKnoxKeeneStale: boolean | null
  isPracticeAgreementStale: boolean | null
  createdDateTime: string
  createdBy: string
  lastUpdatedDateTime: string
  activationRequested: string | null
  practiceInvites: PracticeInvite[]
  practiceTypeDisplayId: string | null // todo: make this an enum across the board
}
interface practiceThunkArgs {
  practiceId: string
  userId: string
  onlyFetchPractice?: boolean
  setDataStatus?: (dataStatus: DataStatus) => void
}
/**
 * Fetches the base practice data for a given practice and updates the Redux store,
 * including tags if the user is an AR and practice roles if the user is an affiliate.
 *
 * @param practiceId - the id of the practice to fetch
 * @param userId - the id of the user making the request, used to determine if we need to fetch practice roles
 * @param onlyFetchPractice - if true, will only fetch the base practice data and skip fetching tags/roles
 * @param setDataStatus - optional callback to set the data status in the calling component, defaults to using the global practice loader data status
 */
export const fetchBasePractice = createAsyncThunk(
  "practiceLoader/fetchBasePractice",
  async (
    { practiceId, userId, onlyFetchPractice, setDataStatus }: practiceThunkArgs,
    { dispatch, getState },
  ) => {
    const setDataStatusEff = (dataStatus: DataStatus) => {
      if (typeof setDataStatus === "function") {
        setDataStatus(dataStatus)
      } else {
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            basePracticeDataStatus: dataStatus,
          }),
        )
      }
    }

    try {
      setDataStatusEff(DataStatus.SENDING)

      const response = await axios.get<BasePracticeResponse>(
        getBasePracticeEndpoint(practiceId),
      )
      if (response.status === 200) {
        const state = getState() as RootState
        const isAffiliateRelations = selectIsAffiliateRelations(state)
        const isAffiliate = selectIsAffiliate(state)
        const practiceData = response.data
        const partialPractice: Partial<Practice> = {
          id: practiceId,
          name: practiceData.name || initialPractice.name,
          legacyAffiliateId: practiceData.legacyAffiliateId,
          status: practiceData.status,
          isVisible: practiceData.isVisible || initialPractice.isVisible,
          websiteUrl: practiceData.websiteUrl || initialPractice.websiteUrl,
          createdBy: practiceData.createdBy,
          createdDateTime: practiceData.createdDateTime,
          overview: practiceData.overview || initialPractice.overview,
          logoImageUrl:
            practiceData.logoImageUrl || initialPractice.logoImageUrl,
          authReleaseRequired: practiceData.authReleaseRequired,
          hasOnlineServicesInCalifornia:
            practiceData.hasOnlineServicesInCalifornia,
          hasNoPhysicalLocations: practiceData.hasNoPhysicalLocations,
          isKnoxKeeneStale: practiceData.isKnoxKeeneStale,
          isPracticeAgreementStale: practiceData.isPracticeAgreementStale,
          // TODO: Would we rather make this it's own slice? Having to update the base might be a pain.
          practiceInvites:
            practiceData.practiceInvites || initialPractice.practiceInvites,
          practiceTypeDisplayId:
            practiceData.practiceTypeDisplayId ??
            PracticeTypeDisplayId.STANDARD,
        }

        // only get tags if user is AR
        // allow by pass for calls that only want to fetch base practice data and update redux without tags/roles
        if (!onlyFetchPractice) {
          if (isAffiliateRelations) {
            const tagsResponse: string[] = await fetchPracticeTags(practiceId)
            partialPractice.tags = tagsResponse.map((tag: string) => tag)
          } else if (isAffiliate) {
            // If we're an affiliate we will then need our practice roles
            dispatch(getPracticeRoles({ userId }))
          }
        }

        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          ...partialPractice,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        setDataStatusEff(DataStatus.LOADED)
        return partialPractice
      } else {
        throw new Error(
          `Failed to fetch base practice with status code: ${response.status}`,
        )
      }
    } catch (error) {
      setDataStatusEff(DataStatus.ERROR)
      appInsightsLogger.traceError(error)
      return undefined
    }
  },
)

export interface PatchArg {
  op: string
  path: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any
}
export interface PatchBasePracticePayload {
  name?: string
  websiteUrl?: string
  overview?: string
  isVisible?: boolean
}
export interface PatchBasePracticeArgs {
  practiceId: string
  payload: PatchBasePracticePayload
  setDataStatus?: (dataStatus: DataStatus) => void
}
export const patchBasePractice = createAsyncThunk(
  "practiceLoader/patchBasePractice",
  async (
    { practiceId, payload, setDataStatus }: PatchBasePracticeArgs,
    { dispatch },
  ) => {
    const patchArg: PatchArg[] = []
    // TODO: implement logo image url
    if (!!payload.name) {
      patchArg.push({ op: "add", path: "/name", value: payload.name })
    }
    if (!!payload.websiteUrl || payload.websiteUrl === "") {
      patchArg.push({
        op: "add",
        path: "/websiteUrl",
        value: payload.websiteUrl,
      })
    }
    if (!!payload.overview || payload.overview === "") {
      patchArg.push({ op: "add", path: "/overview", value: payload.overview })
    }
    if (payload.isVisible !== null && payload.isVisible !== undefined) {
      patchArg.push({
        op: "replace",
        path: "/isVisible",
        value: payload.isVisible,
      })
    }
    try {
      setDataStatus?.(DataStatus.SENDING)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          basePracticeDataStatus: DataStatus.SENDING,
        }),
      )

      const response = await axios.patch(
        getBasePracticeEndpoint(practiceId),
        patchArg,
        { headers: { "Content-Type": "application/json-patch+json" } },
      )
      if (response.status === 200) {
        const partialPractice: Partial<Practice> = {
          id: practiceId,
          ...payload,
        }
        setDataStatus?.(DataStatus.LOADED)
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            basePracticeDataStatus: DataStatus.SAVED,
          }),
        )

        return partialPractice
      } else {
        throw new Error(
          `Failed to patch base practice with status code: ${response.status}`,
        )
      }
    } catch {
      setDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          basePracticeDataStatus: DataStatus.ERROR,
        }),
      )
      console.error("error")

      return {}
    }
  },
)

interface AddressResponse {
  practiceId: string
  line1: string
  line2?: string
  city: string
  stateOrTerritory: string
  postalCode: string
  county: string
  createdDateTime: string
  lastUpdatedDateTime: string
}

interface BillingAddress {
  line1: string
  line2: string
  city: string
  postalCode: string
  county: string
  stateOrTerritory: string
}
interface PostPracticeBillingAddressArgs {
  practiceId: string
  payload: BillingAddress
}
export const getPracticeBillingAddressEndpoint = (
  practiceId: string,
): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/billingaddress`
}
export const fetchBillingAddress = createAsyncThunk(
  "practiceLoader/fetchBillingAddress",
  async (practiceId: string, { dispatch, getState }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        practiceAddressDataStatus: DataStatus.SENDING,
      }),
    )
    try {
      const response = await axios.get<AddressResponse>(
        getPracticeBillingAddressEndpoint(practiceId),
      )
      if (response.status === 200) {
        const state = getState() as { practice: PracticeState }
        const billingAddressData = response.data
        const newBillingAddress: Partial<Practice> = cloneDeep({
          name: state.practice.practice.name,
          practiceInfo: {
            ...state.practice.practice.practiceInfo,
            practiceAddress: {
              addressOne: billingAddressData.line1,
              addressTwo:
                billingAddressData.line2 || initialPracticeAddress.addressTwo,
              city: billingAddressData.city,
              county: billingAddressData.county,
              stateOrTerritory: billingAddressData.stateOrTerritory,
              zipCode: billingAddressData.postalCode,
            },
          },
        })
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          ...newBillingAddress,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceAddressDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error(
          `Failed to fetch billing address with status code: ${response.status}`,
        )
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response &&
        error.response.status === 404
      ) {
        const state = getState() as { practice: PracticeState }
        const partialPractice: Partial<Practice> = cloneDeep({
          practiceInfo: {
            ...state.practice.practice.practiceInfo,
            practiceAddress: initialPracticeAddress,
          },
        })
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          ...partialPractice,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceAddressDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceAddressDataStatus: DataStatus.ERROR,
          }),
        )
        console.error(error)
      }
    }
  },
)

interface AddressResponse {
  practiceId: string
  line1: string
  line2?: string
  city: string
  stateOrTerritory: string
  postalCode: string
  county: string
  createdDateTime: string
  lastUpdatedDateTime: string
}
interface BillingAddress {
  line1: string
  line2: string
  city: string
  postalCode: string
  county: string
  stateOrTerritory: string
}
interface PostPracticeBillingAddressArgs {
  practiceId: string
  payload: BillingAddress
  setDataStatus?: (dataStatus: DataStatus) => void
}
export const putBillingAddress = createAsyncThunk(
  "practiceLoader/putBillingAddress",
  async (
    { practiceId, payload, setDataStatus }: PostPracticeBillingAddressArgs,
    { dispatch, getState },
  ) => {
    try {
      setDataStatus?.(DataStatus.SENDING)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAddressDataStatus: DataStatus.SENDING,
        }),
      )
      const response = await axios.put<AddressResponse>(
        getPracticeBillingAddressEndpoint(practiceId),
        payload,
      )
      if (response.status === 200 || response.status === 201) {
        const state = getState() as RootState

        const billingAddressData = response.data
        const newBillingAddress: Partial<Practice> = cloneDeep({
          practiceInfo: {
            ...state.practice.practice.practiceInfo,
            practiceAddress: {
              addressOne: billingAddressData.line1,
              addressTwo:
                billingAddressData.line2 || initialPracticeAddress.addressTwo,
              city: billingAddressData.city,
              county: billingAddressData.county,
              stateOrTerritory: billingAddressData.stateOrTerritory,
              zipCode: billingAddressData.postalCode,
            },
          },
        })
        setDataStatus?.(DataStatus.LOADED)
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceAddressDataStatus: DataStatus.SAVED,
          }),
        )

        return newBillingAddress
      } else {
        throw new Error(
          `Failed to fetch billing address with status code: ${response.status}`,
        )
      }
    } catch (error) {
      setDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAddressDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)

      return {}
    }
  },
)

export const updatePracticeInfoAddress = createAsyncThunk(
  "practiceProfile/updatePracticeInfoAddress",
  async (
    {
      practiceId,
      payload,
      setDataStatuses,
    }: {
      practiceId: string
      payload: Partial<Practice>
      setDataStatuses?: {
        setPatchPracticeDataStatus?: (status: DataStatus) => void
        setPutPracticeBillingAddressDataStatus?: (status: DataStatus) => void
      }
    },
    { dispatch, getState },
  ) => {
    const {
      setPatchPracticeDataStatus,
      setPutPracticeBillingAddressDataStatus,
    } = setDataStatuses || {}
    const state = getState() as RootState
    let patchBasePracticeReturn: Partial<Practice> = {}
    let putBillingAddressReturn: Partial<Practice> = {}

    const callPatchBasePractice =
      !!payload.name ||
      !!payload.websiteUrl ||
      payload.websiteUrl === "" ||
      !!payload.overview ||
      payload.overview == "" ||
      typeof payload.isVisible === "boolean"

    // make API call to update base practice
    if (callPatchBasePractice) {
      const patchBasePracticeArgs = createPatchBasePracticeArgs(
        practiceId,
        payload,
        state.practice.practice,
        setPatchPracticeDataStatus,
      )
      const patchBasePracticeAction = await dispatch(
        patchBasePractice(patchBasePracticeArgs),
      )
      patchBasePracticeReturn =
        patchBasePracticeAction.payload as Partial<Practice>
    }

    // make API call to update billing address
    if (!!payload.practiceInfo?.practiceAddress) {
      const practiceAddress = payload.practiceInfo.practiceAddress
      const billingAddress = createBillingAddressPayload(practiceAddress)
      const putBillingAddressAction = await dispatch(
        putBillingAddress({
          practiceId,
          payload: billingAddress,
          setDataStatus: setPutPracticeBillingAddressDataStatus,
        }),
      )
      putBillingAddressReturn =
        putBillingAddressAction.payload as Partial<Practice>
    }

    // collect all combined updates from base practice patch and billing address put and update state together
    const updatedPractice: Practice = cloneDeep({
      ...state.practice.practice,
      ...patchBasePracticeReturn,
      ...putBillingAddressReturn,
    })
    dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
  },
)

interface PhoneResponseType {
  id: string
  phoneType: keyof ContactTypeMap
  number: string
  isPrimary: boolean
}
interface EmailResponseType {
  id: string
  contactType: keyof ContactTypeMap
  address: string
  isPrimary: boolean
}
interface FaxResponseType {
  id: string
  contactType: keyof ContactTypeMap
  number: string
  isPrimary: boolean
}
export interface ContactInfoResponse {
  phones: PhoneResponseType[]
  emails: EmailResponseType[]
  faxes: FaxResponseType[]
}
export const fetchContactInfo = createAsyncThunk(
  "practiceLoader/fetchContactInfo",
  async (practiceId: string, { dispatch, getState }) => {
    try {
      const response = await axios.get<ContactInfoResponse>(
        getPracticeContactInfoEndpoint(practiceId),
      )
      if (response.status === 200) {
        const contactInfoData = response.data
        const phones: PhoneEmailFax[] =
          contactInfoData.phones.length > 0
            ? contactInfoData.phones.map((p) => ({
                id: p.id,
                type: p.phoneType,
                value: p.number,
                isPrimary: p.isPrimary,
                error: false,
              }))
            : initialPracticeContact.phones
        const emails: PhoneEmailFax[] =
          contactInfoData.emails.length > 0
            ? contactInfoData.emails.map((e) => ({
                id: e.id,
                type: e.contactType,
                value: e.address,
                isPrimary: e.isPrimary,
                error: false,
              }))
            : initialPracticeContact.altEmails
        const faxes: PhoneEmailFax[] =
          contactInfoData.faxes.length > 0
            ? contactInfoData.faxes.map((f) => ({
                id: f.id,
                type: f.contactType,
                value: f.number,
                isPrimary: f.isPrimary,
                error: false,
              }))
            : initialPracticeContact.faxes
        // Extract the primary email address
        const primaryEmail = emails.find(
          (e: { isPrimary: boolean }) => e.isPrimary,
        ) ?? { value: "" }
        const altEmails = emails.filter(
          (e: { isPrimary: boolean }) => !e.isPrimary,
        )
        if (altEmails.length === 0) {
          altEmails.push({
            id: self.crypto.randomUUID(),
            type: null,
            value: "",
            isPrimary: false,
            error: false,
          })
        }

        const state = getState() as RootState
        const newPracticeContact: Partial<Practice> = cloneDeep({
          practiceInfo: {
            ...state.practice.practice.practiceInfo,
            practiceContact: {
              primaryEmail: primaryEmail.value,
              phones: phones,
              altEmails: altEmails,
              faxes: faxes,
            },
          },
        })
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          ...newPracticeContact,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceContactDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error(
          `Failed to fetch contact info with status code: ${response.status}`,
        )
      }
    } catch (error) {
      console.error(error)
    }
  },
)

interface PostEmailArg {
  contactType: string | null
  address: string
  isPrimary: boolean
}
interface PostPhoneArg {
  phoneType: string | null
  number: string
  isPrimary: boolean
}
interface PostFaxResponseType {
  contactType: string | null
  number: string
  isPrimary: boolean
}
interface PracticeContactArgs {
  emails: PostEmailArg[]
  phones: PostPhoneArg[]
  faxes: PostFaxResponseType[]
}
interface PostPracticeContactArgs {
  practiceId: string
  userId: string
  payload: PracticeContactArgs
  setDataStatus?: (status: DataStatus) => void
}
export const postContactInfo = createAsyncThunk(
  "practiceLoader/postContactInfo",
  async (
    { practiceId, payload, setDataStatus }: PostPracticeContactArgs,
    { dispatch, getState },
  ) => {
    try {
      setDataStatus?.(DataStatus.SENDING)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceContactDataStatus: DataStatus.SENDING,
        }),
      )

      const state = getState() as RootState

      const response = await axios.post<ContactInfoResponse>(
        getPracticeContactInfoEndpoint(practiceId),
        payload,
      )
      if (response.status === 201 || response.status === 200) {
        const contactResponse = response.data
        // NOTE: we need to use the return here to replace any dummy IDs
        const primaryEmail: string =
          payload.emails.find((e: { isPrimary: boolean }) => e.isPrimary)
            ?.address || ""
        const phones: PhoneEmailFax[] =
          contactResponse.phones.length > 0
            ? contactResponse.phones.map((p) => ({
                id: p.id,
                type: p.phoneType,
                value: p.number,
                isPrimary: p.isPrimary,
                error: false,
              }))
            : initialPracticeContact.phones
        const altEmails: PhoneEmailFax[] =
          contactResponse.emails.length > 1
            ? contactResponse.emails
                .filter((e) => !e.isPrimary)
                .map((e) => ({
                  id: e.id,
                  type: e.contactType,
                  value: e.address,
                  isPrimary: e.isPrimary,
                  error: false,
                }))
            : initialPracticeContact.altEmails
        const faxes: PhoneEmailFax[] =
          contactResponse.faxes.length > 0
            ? contactResponse.faxes.map((f) => ({
                id: f.id,
                type: f.contactType,
                value: f.number,
                isPrimary: f.isPrimary,
                error: false,
              }))
            : initialPracticeContact.faxes

        const newPracticeInfo: Partial<Practice> = cloneDeep({
          practiceInfo: {
            ...state.practice.practice.practiceInfo,
            practiceContact: {
              primaryEmail: primaryEmail,
              phones: phones,
              altEmails: altEmails,
              faxes: faxes,
            },
          },
        })
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          ...newPracticeInfo,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        setDataStatus?.(DataStatus.LOADED)
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceContactDataStatus: DataStatus.SAVED,
          }),
        )
        // refetch base practice IF the primary email was updated and practice agreement section is already complete
        // this will grab the agreement stale fields at the base practice level
        const { isPracticeAgreementComplete } =
          selectPracticeCompleteness(state)
        const primaryEmailUpdated =
          primaryEmail !==
          state.practice.practice.practiceInfo.practiceContact?.primaryEmail
        if (primaryEmailUpdated && isPracticeAgreementComplete) {
          const userId = state.user.id
          dispatch(fetchBasePractice({ practiceId, userId }))
        }
        return response.data
      } else {
        throw new Error(
          `Failed to post contact info with status code: ${response.status}`,
        )
      }
    } catch (error) {
      setDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceContactDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)
